
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Team } from "../types";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";

export const useTeamsData = () => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin } = useUserRoles();
  
  console.log("[useTeamsData] Hook called", { userId: user?.id, isSuperAdmin });
  
  const query = useQuery({
    queryKey: ["teams", "withCounts", user?.id, isSuperAdmin],
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      console.log("[useTeamsData] Starting query execution...");
      
      if (!user?.id) {
        console.log("[useTeamsData] No user, returning empty array");
        return [];
      }
      
      try {
        // For super admins, fetch all teams
        if (isSuperAdmin) {
          console.log("[useTeamsData] Super admin - fetching all teams");
          const { data: teams, error } = await supabase
            .from("teams")
            .select(`
              id,
              name,
              description,
              status,
              created_at,
              updated_at,
              image_url,
              team_members_count:team_members(count),
              reports_count:reports(count),
              missions_count:missions(count),
              applications_count:applications(count)
            `)
            .order("created_at", { ascending: false });

          if (error) {
            console.error("[useTeamsData] Supabase error:", error);
            throw error;
          }

          console.log("[useTeamsData] Super admin teams:", teams?.length);
          
          const transformedTeams: Team[] = teams?.map(team => ({
            ...team,
            team_members_count: team.team_members_count?.[0]?.count || 0,
            reports_count: team.reports_count?.[0]?.count || 0,
            missions_count: team.missions_count?.[0]?.count || 0,
            applications_count: team.applications_count?.[0]?.count || 0,
          })) || [];

          return transformedTeams;
        }

        // For regular admins and contributors, get all admins in their bubble
        console.log("[useTeamsData] Regular admin/contributor - fetching admin group");
        
        const { data: adminGroup, error: groupError } = await supabase
          .rpc('get_admin_group', { user_id: user.id });

        if (groupError) {
          console.error("[useTeamsData] Error fetching admin group:", groupError);
          // Fallback to just the current user if RPC fails
          console.log("[useTeamsData] Falling back to current user only");
        }

        // Get all admin IDs in the bubble, or fallback to current user
        const adminIds = adminGroup?.map((g: any) => g.admin_id) || [user.id];
        console.log("[useTeamsData] Admin bubble IDs:", adminIds);
        console.log("[useTeamsData] Number of admins in bubble:", adminIds.length);

        // Fetch teams created by anyone in the admin bubble
        const { data: teams, error } = await supabase
          .from("teams")
          .select(`
            id,
            name,
            description,
            status,
            created_at,
            updated_at,
            image_url,
            created_by,
            team_members_count:team_members(count),
            reports_count:reports(count),
            missions_count:missions(count),
            applications_count:applications(count)
          `)
          .in('created_by', adminIds)
          .order("created_at", { ascending: false });

        console.log("[useTeamsData] Query executed with created_by IN:", adminIds);

        if (error) {
          console.error("[useTeamsData] Supabase error:", error);
          throw error;
        }

        console.log("[useTeamsData] Raw teams data (filtered by RLS):", teams);
        console.log("[useTeamsData] Number of teams:", teams?.length);
        
        // Transform the data to match our Team interface
        const transformedTeams: Team[] = teams?.map(team => ({
          ...team,
          team_members_count: team.team_members_count?.[0]?.count || 0,
          reports_count: team.reports_count?.[0]?.count || 0,
          missions_count: team.missions_count?.[0]?.count || 0,
          applications_count: team.applications_count?.[0]?.count || 0,
        })) || [];

        console.log("[useTeamsData] Transformed teams:", transformedTeams);
        return transformedTeams;
      } catch (err) {
        console.error("[useTeamsData] Query error:", err);
        throw err;
      }
    },
  });

  console.log("[useTeamsData] Query state:", {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    data: query.data
  });

  return query;
};
